import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, AlertCircle, BellRing } from "lucide-react";
import BtcLogo from "../components/BtcLogo";
import { UnsubscribeReason } from "../components/UnsubscribeReason";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const call = async (path, token, method = "POST") => {
  const r = await fetch(`${API}/api/email/${path}?t=${encodeURIComponent(token)}`, { method });
  if (!r.ok) throw new Error("invalid");
  return r.json();
};

function InvalidLink() {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-5" role="alert" data-testid="unsubscribe-invalid">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-slate-500 mt-0.5" aria-hidden="true" />
        <div className="text-sm text-slate-700">
          <div className="font-semibold text-slate-900 mb-1">This unsubscribe link is not valid.</div>
          <p>Sign in and open Profile → Email Preferences to manage the emails you receive.</p>
        </div>
      </div>
    </div>
  );
}

function Status({ off, hasAccount, busy, onResubscribe, token }) {
  return (
    <div className="space-y-5">
      <div
        className={`rounded-xl border p-5 ${off ? "border-emerald-200 bg-emerald-50" : "border-sky-200 bg-sky-50"}`}
        role="status"
        data-testid={off ? "unsubscribe-success" : "resubscribe-success"}
      >
        <div className="flex items-start gap-3">
          {off ? <Check className="w-5 h-5 text-emerald-700 mt-0.5" /> : <BellRing className="w-5 h-5 text-sky-700 mt-0.5" />}
          <div className="text-sm text-slate-800">
            <div className="font-semibold mb-1">
              {off ? "You're unsubscribed from optional emails." : "Optional emails are turned back on."}
            </div>
            <p>
              {off
                ? "You will no longer receive session reminders or new-message alerts."
                : "You'll receive session reminders and new-message alerts again."}
            </p>
          </div>
        </div>
      </div>
      {off && <UnsubscribeReason token={token} />}
      <p className="text-sm text-slate-600" data-testid="unsubscribe-required-note">
        Required emails — account and security notices (such as password resets), application updates, session
        changes and donation receipts — will still be sent so you never miss something important.
      </p>
      <div className="flex flex-wrap gap-3">
        {off && (
          <button type="button" onClick={onResubscribe} disabled={busy} className="btc-btn-primary"
            data-testid="resubscribe-button">
            {busy ? "Saving…" : "Turn optional emails back on"}
          </button>
        )}
        {hasAccount && (
          <Link to="/portal/profile" className="btc-btn-outline" data-testid="unsubscribe-preferences-link">
            Manage email preferences
          </Link>
        )}
      </div>
    </div>
  );
}

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = (params.get("t") || "").trim();
  const [state, setState] = useState("working"); // working | done | error
  const [prefs, setPrefs] = useState(null);
  const [hasAccount, setHasAccount] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return setState("error");
    call("unsubscribe", token)
      .then((d) => { setPrefs(d.preferences); setHasAccount(d.has_account); setState("done"); })
      .catch(() => setState("error"));
  }, [token]);

  const resubscribe = async () => {
    setBusy(true);
    try { setPrefs((await call("resubscribe", token)).preferences); } catch { setState("error"); }
    setBusy(false);
  };

  const off = prefs && !Object.values(prefs).some(Boolean);
  return (
    <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center px-4">
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm w-full max-w-lg p-8 lg:p-10"
        data-testid="unsubscribe-page">
        <Link to="/" className="inline-flex items-center gap-2 mb-6"><BtcLogo className="h-12 w-auto" /></Link>
        <h1 className="text-3xl font-extrabold tracking-tight text-[#0B132B] mb-4">Email preferences</h1>
        {state === "working" && <p className="text-sm text-slate-600" data-testid="unsubscribe-working">Updating your preferences…</p>}
        {state === "error" && <InvalidLink />}
        {state === "done" && <Status off={off} hasAccount={hasAccount} busy={busy} onResubscribe={resubscribe} token={token} />}
      </div>
    </div>
  );
}
